/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
class SynchActionProcessor {
	//The role of the SynchActionProcessor is to apply the same action across the set of widgets in a compare model.
	constructor(compareModel, refChecker) {
		this.compareModel = compareModel;
		this.refChecker = refChecker;

		//A list of actionListeners for all widgetApi's in the comparison.
		this._actionListeners = [];
	}

	initialize(widgetContentApi) {
		this._addWidgetApiListeners(widgetContentApi);
	}

	destroy() {
		this.disable();
	}

	/**
	 * call enable() to listen on widgetApi synchronized action events.
	 */
	enable() {
		this.compareModel.getWidgetContentApis().forEach((widgetContentApi) => {
			this._addWidgetApiListeners(widgetContentApi);
		});
	}

	/**
	 * call disable() to stop listening on all widgetApi synchronized action events.
	 */
	disable() {
		this._actionListeners.forEach((listener) => {
			listener.remove();
		});
		this._actionListeners=[];
	}

	/**
	 * perform a synchronized sort: sort items in both compare widgets the same way according to some selection rules (see synchAction):
	 * @param sourceWidgetId - the widgetId that initiated the action.
	 * @param data - the data (from the widgetApi event that includes this standard information:
	 *		itemContext: what item the action was applied to
	 *		params:	the action-specific parameters
	 *		options: options
	 */
	synchSort(sourceWidgetId, data) {
		const cbSort = (targetDataItemInfo) => {
			const dataItemList = targetDataItemInfo.slotAPI.getDataItemList();
			if (dataItemList && targetDataItemInfo.indexInSlot >= 0 && targetDataItemInfo.indexInSlot < dataItemList.length) {
				const dataItem = dataItemList[targetDataItemInfo.indexInSlot];
				if (dataItem) {
					dataItem.setSort(data.info.value, data.transactionToken);
				}
			}
		};
		return this._synchAction(sourceWidgetId, cbSort, data);
	}

	/**
	 * perform a synchronized format: Format items in both compare widgets the same way according to selection rules (see synchAction):
	 * @param sourceWidgetId - the widgetId that initiated the action.
	 * @param data - the data (from the widgetApi event that includes this standard information:
	 *		itemContext: what item the action was applied to
	 *		params:	the action-specific parameters
	 *		options: options
	 */
	synchFormat(sourceWidgetId, data) {
		const cbFormat = (targetDataItemInfo) => {
			const dataItemList = targetDataItemInfo.slotAPI.getDataItemList();
			if (dataItemList && targetDataItemInfo.indexInSlot >= 0 && targetDataItemInfo.indexInSlot < dataItemList.length) {
				const dataItem = dataItemList[targetDataItemInfo.indexInSlot];
				if (dataItem) {
					dataItem.setFormat(data.info.value, data.transactionToken);
				}
			}
		};
		return this._synchAction(sourceWidgetId, cbFormat, data);
	}

	/**
	 * perform a synchronized topBottom: Top/BottomN items in both compare widgets the same way according to selection rules (see synchAction):
	 * @param sourceWidgetId - the widgetId that initiated the action.
	 * @param data - the data (from the widgetApi event that includes this standard information:
	 *		itemContext: what item the action was applied to
	 *		params:	the action-specific parameters
	 *		options: options
	 */
	synchTopBottom(sourceWidgetId, data) {
		const cbTopBottom = (targetDataItemInfo) => {
			if (data.info.value) {
				targetDataItemInfo.mappedDataItemAPI &&
				targetDataItemInfo.mappedDataItemAPI.setTopBottom(data.info.value, data.transactionToken);
			} else {
				const dataItemList = targetDataItemInfo.slotAPI.getDataItemList();
				if (dataItemList && targetDataItemInfo.indexInSlot >= 0 && targetDataItemInfo.indexInSlot < dataItemList.length) {
					const dataItem = dataItemList[targetDataItemInfo.indexInSlot];
					if (dataItem) {
						dataItem.setTopBottom(null, data.transactionToken);
					}
				}
			}
		};

		//Contextual topBottom shouldn't be done if widgets aren't from the same module.
		return (data && data.info && data.info.value && data.info.value.context && this._widgetsAreFromDifferentModules()) ?
			false :
			this._synchAction(sourceWidgetId, cbTopBottom, data);
	}

	_widgetsAreFromDifferentModules() {
		let firstWidgetModule=null;
		return this.compareModel.getWidgetContentApis().find((widgetContentApi) => {
			if (!firstWidgetModule) {
				firstWidgetModule=widgetContentApi.getFeature('Visualization').getDataSource().getModule();
			} else {
				return widgetContentApi.getFeature('Visualization').getDataSource().getModule() !== firstWidgetModule;
			}
		});
	}

	//Synchronize an action from source widget "widgetId" to any OTHER widgets in the compare model
	//(currently this means the 1 other widget in the compare card but could be more in future)
	//Using a standard set of rules....
	// rule1: if the same itemId appears in both widgets, the action (sort/topBottom/format) will be applied to that itemId wherever it is.
	// rule2: if the same slotId appears in both widgets, the action (sort/topBottom/format) will be applied to the item in that slot.
	_synchAction(sourceWidgetId, cbAction, data) {
		if (this._isSynchInProgress){
			return false;
		}

		if (!this.refChecker.isValid(this.compareModel)) {
			return false;
		}

		this._isSynchInProgress = true;
		let actionApplied=false;
		this.compareModel.getWidgetContentApis().forEach((targetWidgetContentApi) => {
			if (targetWidgetContentApi.getId()!==sourceWidgetId) {
				let targetDataItemInfo=this._findCorrespondingItem(data.info, targetWidgetContentApi);
				if (!targetDataItemInfo) {
					targetDataItemInfo=this._findCorrespondingSlotItem(data.info, targetWidgetContentApi);
				}
				if (targetDataItemInfo) {
					//augment the targetDataItemInfo with the mappedDataItemAPI.
					targetDataItemInfo.mappedDataItemAPI = this._getMappedDataItem(targetWidgetContentApi, targetDataItemInfo);
					cbAction(targetDataItemInfo);
					actionApplied=true;
				}
			}
		});
		this._isSynchInProgress = false;
		return actionApplied;
	}

	//TODO: Change this for R5!
	//Augment the target dataItemInfo payload with the corresponding mappedDataItem
	//The R4 data payload from LiveWidget includes a legacy SlotAPI and index
	//but should change to send a mapped DataItem.
	//Story RTC282463 is in place to do the work to change LW and respond to the change (for all synch actions)
	//We need to use the new setTopBottom API for contextual topN
	//because the context parameter structure changed
	//(and we don't want to know anything about how!)
	_getMappedDataItem(targetWidgetContentApi, targetDataItemInfo) {
		const dataItemAPI = targetDataItemInfo.dataItemAPI;
		if (dataItemAPI) {
			const visualizationAPI = targetWidgetContentApi.getFeature('Visualization');
			const slotsAPI = visualizationAPI && visualizationAPI.getSlots();
			const mappedDataItemAPI = slotsAPI && slotsAPI.getDataItemList().find((mappedDataItemAPI) => { return (mappedDataItemAPI.getId() === dataItemAPI.getId()); });
			return mappedDataItemAPI;
		}
		return null;
	}

	//synchAction helper: Given a "sourceItemContext" (slotAPI and index in the slot)
	//find a corresponding item (by itemId) in the specified targetWidgetApi (if one exists)
	_findCorrespondingItem(sourceItemContext, targetWidgetContentApi) {
		const itemIdToFind = sourceItemContext && sourceItemContext.dataItemId;
		if (!itemIdToFind) {
			return null;
		}
		//Is this itemId found anywhere in the other widget?
		const slotList = targetWidgetContentApi.getFeature('Visualization').getSlots().getSlotList();
		let found = null;
		for(var slot of slotList) {
			const index = slot.getDataItemList().findIndex((dataItemAPI) => {
				return (dataItemAPI.getId()===itemIdToFind || dataItemAPI.getColumnId() === itemIdToFind) ;
			});
			if (index !== -1) {
				found = {
					slotAPI: slot,
					indexInSlot: index,
					dataItemAPI: slot.getDataItemList()[index]
				};
				break;
			}
		}
		return found;
	}

	//synchAction helper: Given a "sourceItemContext" (slotAPI and index in the slot)
	//find a corresponding item (by slotId) in the specified target widgetApi (if one exists)
	_findCorrespondingSlotItem(sourceItemContext, targetWidgetContentApi) {
		const mappingInfo = sourceItemContext && targetWidgetContentApi.getFeature('Visualization')
			.getSlots().getMappingInfo(sourceItemContext.dataItemId);
		const targetSlotAPI = mappingInfo && mappingInfo.slot;
		if (!targetSlotAPI) {
			return null;
		}
		return {
			slotAPI: targetSlotAPI,
			indexInSlot: 0,	//Perform the action on the first item assigned to the slot.
			dataItemAPI: targetSlotAPI.getDataItem()
		};
	}

	//Note: in order to properly remove bindings, we need to store the .bind (currently Events.js only supports a single object as context.)
	_addWidgetApiListeners(widgetContentApi) {
		const visualization = widgetContentApi.getFeature('Visualization');
		this._actionListeners.push(
			visualization.on('change:slots:dataitem:sort', this.synchSort.bind(this, widgetContentApi.getId()))
		);
		this._actionListeners.push(
			visualization.on('change:slots:dataitem:format', this.synchFormat.bind(this, widgetContentApi.getId()))
		);
		this._actionListeners.push(
			visualization.on('change:slots:dataitem:topbottom', this.synchTopBottom.bind(this, widgetContentApi.getId()))
		);
	}
}

export default SynchActionProcessor;



// WEBPACK FOOTER //
// ./src/js/features/compare/SynchActionProcessor.js